import mitt from 'mitt'

enum RemoteDataState {
  NotAsked,
  InProgress,
  Success,
  Failure
}

export type Events = {
  notAsked: string
  inProgress: string
  success: string
  failure: string
  change: keyof Events
}

type DefaultErrorType = string

class NeverError extends Error {
  constructor(value: never) {
    super(`Unknown RemoteData state: ${value}`)
  }
}

class UnwrapError extends Error {
  constructor(value: RemoteDataState) {
    super(`Tried to unwrap when state is not success: state is "${value}"`)
  }
}

class NoPromiseError extends Error {
  constructor() {
    super(`Tried to invoke then whereas RemoteData does not contain promise`)
  }
}

export class RemoteData<T, E = DefaultErrorType> {
  public static states = RemoteDataState

  protected promise: Promise<any> | null = null

  private maps: ((data: T) => any)[] = []

  private eventEmitter = mitt<Events>()

  constructor(
    protected state: RemoteDataState,
    protected value: T | null = null,
    protected error: E | null = null
  ) {
  }

  static notAsked<T, E = DefaultErrorType>() {
    return new RemoteData<T, E>(RemoteDataState.NotAsked)
  }

  static inProgress<T, E = DefaultErrorType>(progress?: T) {
    return new RemoteData<T, E>(RemoteDataState.InProgress, progress || null)
  }

  static success<T, E = DefaultErrorType>(data: T) {
    return new RemoteData<T, E>(RemoteDataState.Success, data)
  }

  static failure<T, E = DefaultErrorType>(error: E, value = null) {
    return new RemoteData<T, E>(RemoteDataState.Failure, value, error)
  }

  static fromPromise<T, E = DefaultErrorType>(promise: Promise<T>) {
    return RemoteData
      .notAsked<T, E>()
      .fromPromise(promise)
  }

  private runMaps() {
    if (
      this.maps.length > 0 &&
      (
        this.isSuccess() ||
        (this.isInProgress() && this.value !== null)
      )
    ) {
      return this.maps.reduce((value, fn) => {
        return fn(value)
      }, this.value as T)
    }
    return this.value
  }

  private emit(event: keyof Events) {
    this.eventEmitter.emit(event, '')
    this.eventEmitter.emit('change', event)
  }

  on(event: keyof Events, handler: () => void) {
    this.eventEmitter.on(event, handler)
    return this
  }

  onChange(handler: (event: keyof Events) => void) {
    this.eventEmitter.on('change', handler)
    return this
  }

  isNotAsked() {
    return this.state === RemoteDataState.NotAsked
  }

  isInProgress() {
    return this.state === RemoteDataState.InProgress
  }

  isSuccess() {
    return this.state === RemoteDataState.Success
  }

  isFailure() {
    return this.state === RemoteDataState.Failure
  }

  notAsked() {
    this.state = RemoteDataState.NotAsked
    this.value = null
    this.error = null
    this.emit('notAsked')
    return this
  }

  inProgress(progress?: T) {
    this.state = RemoteDataState.InProgress
    this.value = progress || null
    this.error = null
    this.emit('inProgress')
    return this
  }

  success(data: T) {
    this.state = RemoteDataState.Success
    this.value = data
    this.error = null
    this.emit('success')
    return this
  }

  failure(error: E, value = null) {
    this.state = RemoteDataState.Failure
    this.value = value
    this.error = error
    this.emit('failure')
    return this
  }

  fromPromise(promise: Promise<T>) {
    this.state = RemoteDataState.InProgress
    this.promise = promise
      .then(
        value => {
          this.success(value)
          return value
        },
        reason => {
          this.failure(reason)
          return Promise.reject(reason)
        }
      )
    return this
  }

  fromRemoteData(remoteData: RemoteData<T, E>) {
    if (remoteData.promise) {
      this.fromPromise(remoteData.promise)
    } else {
      this.state = remoteData.state
      this.value = remoteData.value
      this.error = remoteData.error
    }

    return this
  }

  fold<N = T, P = N, S = N, F = N>(
    notAsked: () => N,
    inProgress: (value: T | null) => P,
    success: (value: T) => S,
    failure: (error: E, value: T | null) => F
  ) {
    switch (this.state) {
      case RemoteDataState.NotAsked:
        return notAsked()
      case RemoteDataState.InProgress:
        return inProgress(this.runMaps() as T)
      case RemoteDataState.Success:
        return success(this.runMaps() as T)
      case RemoteDataState.Failure:
        return failure(this.error as E, this.value)
      default:
        throw new NeverError(this.state)
    }
  }

  map<U>(f: (data: T) => U): RemoteData<U, E> {
    this.maps.push(f)
    return this as any
  }

  unwrap(): T {
    if (this.isSuccess()) {
      return this.runMaps() as T
    } else if (this.isFailure()) {
      throw this.error as E
    } else {
      throw new UnwrapError(this.state)
    }
  }

  then(
    onfulfilled: (value: this) => any,
    onrejected?: (value: this) => any
  ): Promise<this> {
    if (this.promise) {
      const promise = this.promise
        .then(() => onfulfilled(this))

      if (onrejected) {
        promise.catch(() => onrejected(this))
      }

      return promise
    } else {
      throw new NoPromiseError()
    }
  }

  catch(onrejected: (value?: any) => any): Promise<this> {
    if (this.promise) {
      return this.promise
        .catch(() => onrejected(this))
    } else {
      throw new NoPromiseError()
    }
  }
}
